import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { string, node, func, bool, object, array } from 'prop-types';

import { UserNav } from '../../components';

import css from './DashboardLayout.module.css';

/**
 * Reusable layout for dashboard-style pages: header (title + subtitle), sidebar (UserNav), and main content in a card.
 * Use for DashboardPage, ProfileSettingsPage, and any other page that shares this structure.
 *
 * @component
 * @param {string} props.titleId - Intl message id for the page heading (e.g. 'DashboardPage.userHeading')
 * @param {string} props.subtitleId - Intl message id for the subtitle (e.g. 'DashboardPage.userSubtitle')
 * @param {string} props.currentPage - Current page name for UserNav highlight (e.g. 'DashboardPage', 'ProfileSettingsPage')
 * @param {string} [props.activeTab] - Active in-page tab key (only when currentPage is 'DashboardPage')
 * @param {Function} [props.onTabChange] - Callback(tabKey) when tab is selected (only when currentPage is 'DashboardPage')
 * @param {boolean} [props.showAccountPayoutDetails] - Whether to show Payments in nested Account settings (account pages only)
 * @param {boolean} [props.showAccountPaymentMethods] - Whether to show Payment methods in nested Account settings (account pages only)
 * @param {Object} [props.currentUser] - Current user (for dashboard sidebar: provider vs customer tabs)
 * @param {{ id: string, slug: string }} [props.editListingParams] - Listing id and slug for Edit Profile / Availability / Pricing links (provider dashboard)
 * @param {string} [props.currentEditTab] - Current Edit Listing wizard tab (e.g. 'details', 'availability', 'pricing') for sidebar highlight
 * @param {string[]} [props.disabledEditTabs] - Edit Listing tab keys that should be disabled in the sidebar (e.g. ['availability', 'pricing'] when no listing yet)
 * @param {ReactNode} [props.topSection] - Optional content rendered above the main grid (same container width)
 * @param {ReactNode} props.children - Content to render inside the main content card
 * @returns {JSX.Element}
 */
const DashboardLayout = props => {
  const {
    titleId,
    subtitleId,
    currentPage,
    activeTab,
    onTabChange,
    showAccountPayoutDetails,
    showAccountPaymentMethods,
    currentUser,
    editListingParams,
    currentEditTab,
    disabledEditTabs,
    topSection,
    children,
  } = props;

  return (
    <>
      <section className={css.headerSection}>
        <div className={css.container}>
          <div className={css.headerInner}>
            <h1 className={css.headerTitle}>
              <FormattedMessage id={titleId} />
            </h1>
            <p className={css.headerSubtitle}>
              <FormattedMessage id={subtitleId} />
            </p>
          </div>
        </div>
      </section>

      {topSection ? (
        <section className={css.topSection}>
          <div className={css.container}>{topSection}</div>
        </section>
      ) : null}

      <section className={css.contentSection}>
        <div className={css.container}>
          <div className={css.grid}>
            <aside className={css.sidebar}>
              <UserNav
                variant="dashboardSidepanel"
                currentPage={currentPage}
                currentUser={currentUser}
                activeTab={activeTab}
                onTabChange={onTabChange}
                showAccountPayoutDetails={showAccountPayoutDetails}
                showAccountPaymentMethods={showAccountPaymentMethods}
                editListingParams={editListingParams}
                currentEditTab={currentEditTab}
                disabledEditTabs={disabledEditTabs}
              />
            </aside>

            <div className={css.main}>
              <div className={css.contentCard}>{children}</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

DashboardLayout.propTypes = {
  titleId: string.isRequired,
  subtitleId: string.isRequired,
  currentPage: string.isRequired,
  activeTab: string,
  onTabChange: func,
  showAccountPayoutDetails: bool,
  showAccountPaymentMethods: bool,
  currentUser: object,
  editListingParams: object,
  currentEditTab: string,
  disabledEditTabs: array,
  topSection: node,
  children: node.isRequired,
};

export default DashboardLayout;
